import qs from 'qs';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { DateTime } from 'luxon';
import { decodeUrl, encodeUrl } from './base64';

const query = qs.parse(window.location.search.substring(1));

interface KeycloakOpts {
  url: string;
  realm: string;
  clientId: string;
}

export interface FlipOIDCUser {
  name: string;
  externalId: string;
  preferredUsername: string;
  givenName: string;
  familyName: string;
  tenant: string;
  email: string;
}

export interface KeycloakTokenResponse {
  access_token: string;
  expires_in: number;
  'not-before-policy': number;
  refresh_expires_in: number;
  refresh_token: string;
  scope: string;
  session_state: string;
  token_type: 'Bearer';
}

export interface KeycloakAccessTokenDecoded {
  iat: number;
  auth_time: number;
  jti: string;
  iss: string;
  aud: string;
  sub: string;
  typ: string;
  azp: string;
  session_state: string;
  'allowed-origins': string[];
  realm_access: {
    roles: string[];
  };
  resource_access: {
    account: {
      roles: string[];
    };
  };
  scope: string;
  sid: string;
  clientId: string;
  email_verified: boolean;
  roles: string[];
  name: string;
  external_id: string;
  preferred_username: string;
  given_name: string;
  family_name: string;
  tenant: string;
  email: string;
}

export default class Keycloak {
  url: string;

  realm: string;

  clientId: string;

  token: string;

  authenticated: boolean;

  user: FlipOIDCUser;

  expiresAt: DateTime;

  refreshToken: string;

  state: Record<string, any>;

  constructor({ url, realm, clientId }: KeycloakOpts) {
    this.url = url;
    this.realm = realm;
    this.clientId = clientId;
    this.token = '';
    this.authenticated = false;
    this.user = undefined;
    this.expiresAt = undefined;
    this.refreshToken = undefined;
    this.state = {};
  }

  static extractUser(token: string) {
    const jwt = jwtDecode<KeycloakAccessTokenDecoded>(token);
    return {
      name: jwt.name,
      externalId: jwt.external_id,
      preferredUsername: jwt.preferred_username,
      givenName: jwt.given_name,
      familyName: jwt.family_name,
      tenant: jwt.tenant,
      email: jwt.email,
    };
  }

  async init() {
    if (!query.code) {
      if (!(this.clientId && this.url && this.realm)) {
        throw new Error('clientId, url and realm is required.');
      }

      const url = new URL(`${this.url}/auth/realms/${this.realm}/protocol/openid-connect/auth`);
      url.searchParams.append('client_id', this.clientId);
      url.searchParams.append('response_type', 'code');
      url.searchParams.append('state', encodeUrl(JSON.stringify({
        query,
        clientId: this.clientId,
        realm: this.realm,
        url: this.url,
        path: document.location.pathname,
      })));
      window.location.href = url.toString();
      return undefined;
    }

    if (query.code) {
      if (query.state) {
        try {
          this.state = JSON.parse(decodeUrl(query.state.toString()));
        } catch {
          // Do nothing
        }
      }

      const { clientId, realm, url } = this.state || {};

      const params = new URLSearchParams({
        code: query.code.toString(),
        client_id: clientId,
        grant_type: 'authorization_code',
      });

      const { data } = await axios.post<KeycloakTokenResponse>(`${url}/auth/realms/${realm}/protocol/openid-connect/token`, params);

      const jwt = jwtDecode<KeycloakAccessTokenDecoded>(data.access_token);

      this.token = data.access_token;
      this.refreshToken = data.refresh_token;
      this.expiresAt = DateTime.now().plus({ seconds: data.expires_in });

      this.user = {
        name: jwt.name,
        externalId: jwt.external_id,
        preferredUsername: jwt.preferred_username,
        givenName: jwt.given_name,
        familyName: jwt.family_name,
        tenant: jwt.tenant,
        email: jwt.email,
      };

      this.authenticated = true;
    }

    return undefined;
  }
}
